import React, {Fragment, useContext, useEffect, useState} from 'react';
import DataTable, {TableColumn} from 'react-data-table-component';
import {Download, Eye} from 'react-feather';
import IconBtn from '../../../../CommonElements/IconButton';
import Loader from '../../../../Layout/Loader';
import AuthorizationContext from '../../../../_helper/Authorization';
import type {AuthorizationContextType} from '../../../../_helper/Authorization/AuthorizationTypes';
import MultimediaService from '../Services/Multimedia.service';
import MultimediaPreview from './MultimediaPreview';
import type {Multimedia, MultimediaTableOptions, MultimediaTableRef} from '../Types/MultimediaTypes';

interface MultimediaTableProps {
    params?: Record<string, unknown>;
    tableOptions?: MultimediaTableOptions;
}

const MultimediaTable = React.forwardRef<MultimediaTableRef, MultimediaTableProps>(({
                                                                                        params = {},
                                                                                        tableOptions = {
                                                                                            delete: true,
                                                                                            download: true,
                                                                                            view: true
                                                                                        }
                                                                                    }, ref) => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [multimediaList, setMultimediaList] = useState<Multimedia[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [totalRows, setTotalRows] = useState<number>(0);
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler} = authContext || {};

    React.useImperativeHandle(ref, () => ({
        getMultimedia,
        addMultimedia,
    }));

    const getMultimedia = (page: number = currentPage, limit: number = perPage) => {
        setLoaderVisibility(true);
        MultimediaService.fetchMultimedia({...params, page, per_page: limit})
            .then((response) => {
                setMultimediaList(response.data || []);
                if (response.meta) {
                    setCurrentPage(response.meta.current_page);
                    setPerPage(response.meta.per_page);
                    setTotalRows(response.meta.total);
                } else {
                    setCurrentPage(page);
                    setPerPage(limit);
                    setTotalRows(response.data?.length || 0);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    };

    useEffect(() => {
        getMultimedia(1, perPage);
    }, [params]);

    const addMultimedia = (multimedia: Multimedia) => {
        const alreadyExists = multimediaList.some((item) => item.id === multimedia.id);

        setMultimediaList((currentList) => {
            const nextList = [...currentList];
            const currentIndex = nextList.findIndex((item) => item.id === multimedia.id);

            if (currentIndex >= 0) {
                nextList[currentIndex] = multimedia;
                return nextList;
            }

            return [multimedia, ...nextList];
        });

        if (!alreadyExists) {
            setTotalRows((currentTotal) => currentTotal + 1);
        }
    };

    const handleDelete = async (multimedia: Multimedia) => {
        try {
            setLoaderVisibility(true);
            await MultimediaService.deleteMultimedia(multimedia.id);
            setMultimediaList((currentList) => currentList.filter((item) => item.id !== multimedia.id));
            setTotalRows((currentTotal) => Math.max(currentTotal - 1, 0));
        } catch (exception: any) {
            errorHandler(exception);
        } finally {
            setLoaderVisibility(false);
        }
    };


    const columns: TableColumn<Multimedia>[] = [
        {
            name: 'Preview',
            cell: (row: Multimedia) => (
                <div style={{minWidth: 220}}>
                    <MultimediaPreview
                        multimedia={row}
                        showActions={false}
                    />
                </div>
            ),
            grow: 2,
        },
        {
            name: 'File',
            selector: (row: Multimedia) => `${MultimediaService.resolveFileName(row)} | ${row.mime_type || row.extension || ''}`,
            sortable: false,
            grow: 2,
        },
        {
            name: 'Role',
            selector: (row: Multimedia) => row.role,
            sortable: false,
        },
        {
            name: 'Option',
            cell: (row: Multimedia) => (
                <div className='d-flex align-items-center gap-2'>
                    {tableOptions?.view !== false && (
                        <IconBtn
                            type='default'
                            icon={<Eye size={16}/>}
                            attrBtn={{
                                onClick: () => MultimediaService.viewMultimedia(row),
                                color: 'link',
                                className: 'p-0'
                            }}
                            permission='read-multimedia'
                        />
                    )}
                    {tableOptions?.download !== false && (
                        <IconBtn
                            type='default'
                            icon={<Download size={16}/>}
                            attrBtn={{
                                onClick: () => MultimediaService.downloadMultimedia(row),
                                color: 'link',
                                className: 'p-0'
                            }}
                            permission='read-multimedia'
                        />
                    )}
                    {tableOptions?.delete !== false && (
                        <IconBtn
                            type='delete'
                            confirm={{text: 'Once deleted, you will not be able to recover this file!'}}
                            onclick={() => handleDelete(row)}
                            permission='delete-multimedia'
                        />
                    )}
                </div>
            ),
            right: true,
        },
    ];

    return (
        <Fragment>
            <Loader visibility={loaderVisibility}/>
            <DataTable
                pagination
                paginationServer
                columns={columns}
                data={multimediaList}
                paginationTotalRows={totalRows}
                paginationPerPage={perPage}
                paginationDefaultPage={currentPage}
                onChangePage={(page) => getMultimedia(page, perPage)}
                onChangeRowsPerPage={(newPerPage, page) => {
                    setPerPage(newPerPage);
                    getMultimedia(page, newPerPage);
                }}
                highlightOnHover={true}
                striped={true}
                responsive={true}
            />
        </Fragment>
    );
});

export default MultimediaTable;



